import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// components
import NotesSection from "./notes";
import ScheduleMeeting from "./meeting";
import EventTab from "./events";
import NoStudentBox from "../noStudentBox";

import { getData, loadingIndividualStudentDetails } from "@/store/actions/studentDetailsActions";

// mui
import { Box, makeStyles, CircularProgress, Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    buttonWrap: {
        display: "flex",
        gap: theme.spacing(2),
    },
    button: {
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        padding: theme.spacing(1, 2),
        background: theme.palette.common.white,
        position: "relative",
    },
    outline: {
        height: "100%",
        position: "absolute",
        width: "100%",
        left: 0,
        top: 0,
        border: `2px solid ${theme.palette.primary.main}`,
        borderBottomColor: "white",
    },
    tabPanel: {
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(2),
        borderRadius: "6px",
        border: `2px solid ${theme.palette.primary.main}`,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
    },
    activeButton: {
        color: `${theme.palette.primary.main}!important`,
        fontWeight: 600,
        marginBottom: "-2px",
        paddingBottom: "10px",
    },
    disabled: {
        color: `${theme.palette.text.disabled}!important`,
        pointerEvents: "none",
        marginBottom: "0px!important",
        paddingBottom: "8px!important",
    },
    maxHeight: {
        maxHeight: "851px",
        overflow: "hidden scroll",
    },
}));

export default function SideBar({ studentData }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [value, setValue] = React.useState(0);
    const universityDetails = useSelector((state) => state.universityDetails.data);
    const loading = useSelector((state) => state.studentDetails.loading);
    const upcomingEventsLoading = useSelector((state) => state.upcomingEvents.loading);
    const registeredEventsLoading = useSelector((state) => state.registeredEvents.loading);
    const meetingsLoading = useSelector((state) => state.meetings.loading);
    const counsellorMeetingsLoading = useSelector((state) => state.meetings.counsellorLoading);
    const notesLoading = useSelector((state) => state.notes.loading);

    const handleChange = (newValue, scrollTop) => {
        setValue(newValue);
        if (scrollTop) {
            window.scroll(0, 0);
        }
    };

    React.useEffect(() => {
        handleChange(0);
        if (!studentData) return;
        dispatch(
            getData(
                studentData.userId,
                studentData.registrantCode,
                universityDetails.universityCode,
                universityDetails.fairID,
                studentData
            )
        );
    }, [studentData]);

    React.useEffect(() => {
        if (!loading) return;

        if (
            upcomingEventsLoading ||
            registeredEventsLoading ||
            meetingsLoading ||
            counsellorMeetingsLoading ||
            notesLoading
        ) {
            return;
        }
        dispatch(loadingIndividualStudentDetails(false));
    }, [
        loading,
        upcomingEventsLoading,
        registeredEventsLoading,
        meetingsLoading,
        notesLoading,
        counsellorMeetingsLoading,
    ]);

    if (!studentData) {
        return (
            <Box mt={4}>
                <NoStudentBox />
            </Box>
        );
    }

    const renderTabs = () => {
        if (!universityDetails.showPU) {
            return (
                <Box className={classes.buttonWrap}>
                    <Typography
                        className={`${classes.button} ${value === 0 ? classes.activeButton : ""} ${
                            loading ? classes.disabled : ""
                        }`}
                        onClick={() => handleChange(0)}
                    >
                        <span className={value === 0 ? classes.outline : ""}></span>
                        {"Internal notes"}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box className={classes.buttonWrap} justifyContent={"space-between"}>
                <Typography
                    className={`${classes.button} ${value === 0 ? classes.activeButton : ""} ${
                        loading ? classes.disabled : ""
                    }`}
                    onClick={() => handleChange(0)}
                >
                    <span className={value === 0 && !loading ? classes.outline : ""}></span>
                    {"Internal notes"}
                </Typography>
                <Typography
                    className={`${classes.button} ${value === 1 ? classes.activeButton : ""} ${
                        loading ? classes.disabled : ""
                    }`}
                    onClick={() => handleChange(1)}
                >
                    <span className={value === 1 && !loading ? classes.outline : ""}></span>
                    {"Next steps"}
                </Typography>
                <Typography
                    className={`${classes.button} ${value === 2 ? classes.activeButton : ""} ${
                        loading ? classes.disabled : ""
                    }`}
                    onClick={() => handleChange(2)}
                >
                    <span className={value === 2 && !loading ? classes.outline : ""}></span>
                    {"Upcoming events"}
                </Typography>
            </Box>
        );
    };

    return (
        <Box>
            {renderTabs()}
            {loading ? (
                <Box mt={4} textAlign="center">
                    <CircularProgress color={"primary"} size={30} />
                </Box>
            ) : (
                <>
                    <div hidden={value !== 0} role="tabpanel" className={classes.tabPanel}>
                        <NotesSection
                            registrantCode={studentData.registrantCode}
                            changeTab={() => {
                                if (universityDetails.showPU) {
                                    handleChange(1, true);
                                }
                            }}
                        />
                    </div>
                    <div hidden={value !== 1} role="tabpanel" className={classes.tabPanel}>
                        <ScheduleMeeting
                            studentData={studentData}
                            changeTab={() => {
                                handleChange(2, true);
                            }}
                        />
                    </div>
                    <div
                        hidden={value !== 2}
                        role="tabpanel"
                        className={`${classes.tabPanel} ${classes.maxHeight}`}
                    >
                        <EventTab studentData={studentData} />
                    </div>
                </>
            )}
        </Box>
    );
}

SideBar.propTypes = {
    studentData: PropTypes.object,
};
